<template>
  <div :key="device?.mac + device?.currentStatus">
   <InventoryPageHeader>
  <template #title>
    <router-link to="/devices" class="btn btn-sm btn-outline-secondary mr-2">
      <i class="fas fa-arrow-left"></i>
    </router-link>
    Device Details
  </template>
</InventoryPageHeader>

    <b-container fluid class="mt--6">
      <div v-if="loading" class="text-center py-5"><b-spinner variant="primary" /></div>
      <b-alert v-else-if="!device" show variant="danger">Device not found.</b-alert>

      <template v-else>
        <b-card no-body class="card-shadow mb-4">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <span><i class="fas fa-microchip mr-2"></i>Device: <strong>{{ device.mac }}</strong></span>
            <b-badge :variant="statusBadge(device.currentStatus)">{{ device.currentStatus }}</b-badge>
          </b-card-header>
          <b-card-body>
            <b-row>
              <b-col md="6">
                <dl>
                  <dt>Current Location</dt>
                  <dd>
                    <router-link
                      :to="`/companies/${encodeURIComponent(device.currentLocation)}`"
                      class="text-primary font-weight-bold"
                    >
                      {{ device.currentLocation }}
                    </router-link>
                  </dd>
                  <template v-if="device.dueDate">
                    <dt>Expected Return</dt>
                    <dd>
                      <span :class="overdueClass">{{ formatDueDate(device.dueDate) }}</span>
                      <small class="ml-2" :class="overdueClass">({{ timeLeftText }})</small>
                    </dd>
                  </template>
                  <dt>Batch</dt>
                  <dd>
                    <dt>Owner / Distributor</dt>
<dd>{{ device.owner || '—' }}</dd>
                    <router-link
                      v-if="batchNum"
                      :to="`/batches/${batchNum}`"
                      class="text-primary font-weight-bold"
                    >
                      {{ device.batch }}
                    </router-link>
                  </dd>
                </dl>
              </b-col>
              <b-col md="6">
                <dl>
                  <dt>Quality Issues</dt>
                  <dd>{{ device.qualityIssues || '—' }}</dd>
                  <dt>Notes</dt>
                  <dd>{{ device.notes || '—' }}</dd>
                </dl>
              </b-col>
            </b-row>

            <!-- Clean button row -->
            <div class="mt-3 d-flex align-items-center flex-wrap" style="gap:8px;">
              <base-button size="sm" type="primary" @click="openSendModal">
                <i class="fas fa-paper-plane"></i> Send to Company
              </base-button>
              <base-button
                v-if="showReturnButton"
                size="sm" type="success" @click="openReturnModal"
              >
                <i class="fas fa-undo-alt"></i> Return from Company
              </base-button>

              <b-dropdown size="sm" variant="outline-secondary" toggle-class="btn-sm" right>
                <template #button-content>
                  <i class="fas fa-ellipsis-v"></i>
                </template>
                <b-dropdown-item @click="openAddEvent">
                  <i class="fas fa-plus mr-2"></i> Add Event
                </b-dropdown-item>
                <b-dropdown-item @click="markRMA">
                  <i class="fas fa-tools mr-2"></i> Mark RMA
                </b-dropdown-item>
                <b-dropdown-item @click="decommission">
                  <i class="fas fa-archive mr-2"></i> Decommission
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </b-card-body>
        </b-card>

        <b-card no-body class="card-shadow">
          <b-card-header><i class="fas fa-history mr-2"></i>Event Timeline</b-card-header>
          <b-card-body class="p-0 table-responsive">
            <el-table :data="timeline" style="width:100%;">
              <el-table-column prop="date" label="Date" />
              <el-table-column label="Event Type">
                <template slot-scope="{ row }">
                  <b-badge :variant="eventBadge(row.type)">{{ row.type }}</b-badge>
                </template>
              </el-table-column>
              <el-table-column prop="details" label="Details" />
            </el-table>
          </b-card-body>
        </b-card>

        <!-- Send to Company Modal -->
        <modal :show="showSendModal" @close="showSendModal = false">
          <template v-slot:header><h5>Send Device to Company</h5></template>
          <b-form-group label="Destination Company *">
            <b-form-select v-model="sendCompany" :options="companyOptions">
              <template #first><b-form-select-option :value="null" disabled>Select company...</b-form-select-option></template>
            </b-form-select>
          </b-form-group>
          <b-form-group label="New Status">
            <b-form-select v-model="sendStatus" :options="sendStatusOptions" />
            <small class="text-muted">Set the device status after sending (e.g., keep as Test or mark as Sold).</small>
          </b-form-group>
          <b-form-group v-if="sendStatus === 'Test'" label="Duration (days)">
            <b-form-input v-model.number="sendDuration" type="number" min="1" placeholder="e.g., 30" />
            <small class="text-muted">How many days will the device be with this company?</small>
          </b-form-group>
          <b-form-group label="Notes (optional)">
            <b-form-textarea v-model="sendNotes" rows="2" placeholder="e.g. Loan for testing, Sold with discount..." />
          </b-form-group>
          <template v-slot:footer>
            <base-button type="outline-secondary" @click="showSendModal = false">Cancel</base-button>
            <base-button type="primary" :disabled="!sendCompany" @click="sendToCompany">Send</base-button>
          </template>
        </modal>

        <!-- Return from Company Modal -->
        <modal :show="showReturnModal" @close="showReturnModal = false">
          <template v-slot:header><h5>Return Device from Company</h5></template>
          <b-form-group label="New Status">
            <b-form-select v-model="returnStatus" :options="returnStatusOptions" />
            <small class="text-muted">Set the device status after return (e.g., OK, Test).</small>
          </b-form-group>
          <b-form-group label="Notes (optional)">
            <b-form-textarea v-model="returnNotes" rows="2" placeholder="e.g. Minor wear, awaiting inspection..." />
          </b-form-group>
          <template v-slot:footer>
            <base-button type="outline-secondary" @click="showReturnModal = false">Cancel</base-button>
            <base-button type="primary" @click="returnFromCompany">Confirm Return</base-button>
          </template>
        </modal>

        <!-- Add Event Modal -->
        <modal :show="showAddEventModal" @close="showAddEventModal = false">
          <template v-slot:header><h5>Add Timeline Event</h5></template>
          <b-form-group label="Date"><b-form-input type="date" v-model="newEvent.date" /></b-form-group>
          <b-form-group label="Event Type">
            <b-form-select v-model="newEvent.type" :options="eventTypes" />
          </b-form-group>
          <b-form-group label="Details"><b-form-textarea v-model="newEvent.details" rows="3" placeholder="Describe what happened..." /></b-form-group>
          <template v-slot:footer>
            <base-button type="outline-secondary" @click="showAddEventModal = false">Cancel</base-button>
            <base-button type="primary" @click="addEvent">Add Event</base-button>
          </template>
        </modal>
      </template>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import InventoryPageHeader from '@/components/InventoryPageHeader.vue'

export default {
  name: 'DeviceDetail',
  components: { InventoryPageHeader },

  props: ['mac'],
  data() {
    return {
      loading: true,
      showAddEventModal: false,
      showSendModal: false,
      showReturnModal: false,
      newEvent: { date: '', type: 'Sent to company', details: '' },
      sendCompany: null,
      sendNotes: '',
      sendStatus: 'Test',
      sendDuration: 30,
      returnStatus: 'OK',
      returnNotes: '',
      eventTypes: [
        { value: 'Sent to company', text: 'Sent to company' },
        { value: 'Returned from company', text: 'Returned from company' },
        { value: 'Repair', text: 'Repair' },
        { value: 'RMA', text: 'RMA' },
        { value: 'Issue Found', text: 'Issue Found' },
        { value: 'Received from Factory', text: 'Received from Factory' },
        { value: 'Other', text: 'Other' }
      ]
    }
  },
  computed: {
    ...mapState('inventory', ['devices', 'companies']),
    device() {
      return this.devices.find(d => d.mac === this.mac)
    },
    timeline() {
      if (!this.device) return []
      return buildTimelineFromNotes(this.device)
    },
    batchNum() {
      if (!this.device?.batch) return null
      const m = this.device.batch.match(/^Batch\s+(\S+)/)
      return m ? m[1] : null
    },
    companyOptions() {
      return this.companies.map(c => ({ value: c, text: c }))
    },
    sendStatusOptions() {
      return [
        { value: 'Test', text: 'Test (active)' },
        { value: 'OK', text: 'OK' },
        { value: 'Sold', text: 'Sold' },
        { value: 'Decommissioned', text: 'Decommissioned' }
      ]
    },
    returnStatusOptions() {
      return [
        { value: 'OK', text: 'OK' },
        { value: 'Test', text: 'Test' }
      ]
    },
    timeLeftText() {
      if (!this.device?.dueDate) return ''
      const now = new Date()
      const due = new Date(this.device.dueDate)
      const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      if (diff > 0) return `${diff} days left`
      if (diff === 0) return 'due today'
      return `overdue by ${Math.abs(diff)} days`
    },
    overdueClass() {
      if (!this.device?.dueDate) return ''
      const now = new Date()
      const due = new Date(this.device.dueDate)
      return due < now ? 'text-danger font-weight-bold' : 'text-success'
    },
    showReturnButton() {
      const stockLocs = ['InTech stock', 'Freiburg HQ', 'Freiburg office', 'Freiburg office (demo unit)', 'HQ Freiburg']
      return !stockLocs.includes(this.device?.currentLocation)
    }
  },
  mounted() {
    this.$store.dispatch('inventory/loadDevices').then(() => { this.loading = false })
    this.$store.dispatch('inventory/loadCompanies')
  },
  methods: {
    ...mapActions('inventory', ['updateDevice']),
    statusBadge(s) {
      if (s.startsWith('RMA')) return 'danger'
      if (s === 'OK') return 'success'
      if (s === 'Test') return 'info'
      if (s === 'Sold') return 'secondary'
      if (s === 'Decommissioned') return 'dark'
      return 'secondary'
    },
    eventBadge(type) {
      const t = type.toLowerCase()
      if (t.includes('received') || t.includes('factory')) return 'primary'
      if (t.includes('delivered') || t.includes('sent')) return 'info'
      if (t.includes('returned')) return 'success'
      if (t.includes('repair')) return 'warning'
      if (t.includes('issue')) return 'danger'
      if (t.includes('rma')) return 'secondary'
      return 'dark'
    },
    formatDueDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit' })
    },
    openAddEvent() {
      this.newEvent.date = new Date().toISOString().split('T')[0]
      this.newEvent.type = 'Sent to company'
      this.newEvent.details = ''
      this.showAddEventModal = true
    },
    addEvent() {
      if (!this.newEvent.date || !this.newEvent.type) return
      const entry = `${this.newEvent.date} - ${this.newEvent.type}: ${this.newEvent.details}`
      const newNotes = this.device.notes ? this.device.notes + '. ' + entry : entry
      this.updateDevice({ ...this.device, notes: newNotes })
      this.showAddEventModal = false
    },
    openSendModal() {
      this.sendCompany = null
      this.sendNotes = ''
      this.sendStatus = 'Test'
      this.sendDuration = 30
      this.showSendModal = true
    },
    sendToCompany() {
      if (!this.sendCompany) return
      const today = new Date().toISOString().split('T')[0]

      // Determine the correct event type based on status
      let eventType = 'Sent to company'
      if (this.sendStatus === 'Sold') eventType = 'Sold to company'
      else if (this.sendStatus === 'Test') eventType = 'Sent for testing'
      // (OK or other statuses keep the default)

      const dueDate = (this.sendStatus === 'Test' && this.sendDuration)
        ? new Date(Date.now() + this.sendDuration * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : null
      const entry = `${today} - ${eventType}: ${this.sendCompany}` + (this.sendNotes ? ' - ' + this.sendNotes : '')
      const newNotes = this.device.notes ? this.device.notes + '. ' + entry : entry
      this.updateDevice({
        ...this.device,
        currentLocation: this.sendCompany,
        currentStatus: this.sendStatus,
        notes: newNotes,
        dueDate: dueDate || this.device.dueDate
      })
      this.showSendModal = false
      this.sendCompany = null
      this.sendNotes = ''
      this.sendStatus = 'Test'
      this.sendDuration = 30
    },
    openReturnModal() {
      this.returnStatus = 'OK'
      this.returnNotes = ''
      this.showReturnModal = true
    },
    returnFromCompany() {
      const today = new Date().toISOString().split('T')[0]
      const entry = `${today} - Returned from company: ${this.device.currentLocation}` + (this.returnNotes ? ' - ' + this.returnNotes : '')
      const newNotes = this.device.notes ? this.device.notes + '. ' + entry : entry
      this.updateDevice({
        ...this.device,
        currentLocation: 'InTech stock',
        currentStatus: this.returnStatus,
        notes: newNotes,
        dueDate: null
      })
      this.showReturnModal = false
      this.returnNotes = ''
    },
    markRMA() {
  const today = new Date().toISOString().split('T')[0]
  const entry = `${today} - Marked for RMA: ${this.device.currentLocation}`
  const newNotes = this.device.notes ? this.device.notes + '. ' + entry : entry
  this.updateDevice({
    ...this.device,
    currentStatus: 'RMA',
    notes: newNotes
  })
},
    decommission() {
      this.updateDevice({ ...this.device, currentStatus: 'Decommissioned' })
    }
  }
}

// ---- Helper function to build timeline from device notes ----
function buildTimelineFromNotes(device) {
  const events = []
  events.push({
    date: '2025-04-01',
    type: 'Received from Factory',
    details: device.batch || 'Unknown batch'
  })
  if (device.qualityIssues) {
    events.push({
      date: '2025-06-15',
      type: 'Issue Found',
      details: device.qualityIssues
    })
  }
  if (device.notes) {
    const parts = device.notes.split('. ')
    parts.forEach((part, idx) => {
      const clean = part.trim()
      if (!clean) return
      const match = clean.match(/^(\d{4}-\d{2}-\d{2})\s*-\s*(.+?):\s*(.*)/)
      if (match) {
        events.push({ date: match[1], type: match[2], details: match[3] || '' })
      } else {
        events.push({
          date: `2025-0${4 + idx}-01`,
          type: idx === 0 ? 'Delivered' : (idx === 1 ? 'Repair' : 'Returned'),
          details: clean
        })
      }
    })
  }
  events.sort((a, b) => b.date.localeCompare(a.date))
  return events.slice(0, 10)
}
</script>